import React, {memo, ReactNode, useMemo} from 'react';
import { useSelector } from 'react-redux';
import {ControlModel, ControlTypeModel, ThreePhaseSet, CustomIconParameters} from '../../types/control';
import upButton from '../../assets/images/icons/controls/up-button.png';
import equalButton from '../../assets/images/icons/controls/equal-button.png';
import downButton from '../../assets/images/icons/controls/down-button.png';
import {Column} from '../Column';
import {Widget} from '../Widget';
import './index.scss';
import {PhaseStatus} from './index';
import {onClickHandler} from '../../utils/onclick';
import { Configs } from '../../configs';

type Props = {
    control: ControlModel;
    click: (curtainStatus: PhaseStatus) => void;
    children?: ReactNode;
    controlType: ControlTypeModel<ThreePhaseSet>;
};

export const ThreePhaseControlComponent = memo(function ThreePhaseControlComponent(props: Props) {
    const {control, children, click, controlType} = props;
    const controlIconTypeList = useSelector(state => state.control.controlIconTypeList);

    const isTwoPhase = controlType.render_type === 'two_phase';

    const curtainStatus = control.current_value as PhaseStatus;

    const icon = useMemo(() => {
        const { parameters } = control;
        if ((parameters as CustomIconParameters)?.is_custom_icon && (parameters as CustomIconParameters).custom_icon?.custom_icon_id) {
            const customParams = parameters as CustomIconParameters;
            const customIcon = controlIconTypeList.find(icon => icon.id === customParams.custom_icon?.custom_icon_id);
            if (customIcon) {
                if (curtainStatus === PhaseStatus.Up) {
                    return Configs.IMAGE_PREFIX + customIcon.image_set.up;
                }
                if (curtainStatus === PhaseStatus.Down) {
                    return Configs.IMAGE_PREFIX + customIcon.image_set.down;
                }
                if (curtainStatus === PhaseStatus.Stop) {
                    return Configs.IMAGE_PREFIX + customIcon.image_set.stop;
                }
            }
        } else {
            if (curtainStatus === PhaseStatus.Up) {
                return controlType.image_set.up;
            }
            if (curtainStatus === PhaseStatus.Down) {
                return controlType.image_set.down;
            }
            if (curtainStatus === PhaseStatus.Stop) {
                return controlType.image_set.stop;
            }
        }
        return null;
    }, [controlType, curtainStatus, controlIconTypeList, control]);


    const actionIcons = useMemo(() => {
        const { parameters } = control;
        if ((parameters as CustomIconParameters)?.is_custom_icon) {
            const customParams = parameters as CustomIconParameters;
            const getActionIcon = (actionIconId: string | undefined) => {
                if (actionIconId) {
                    const actionIcon = controlIconTypeList.find(icon => icon.id === actionIconId);
                    if (actionIcon) {
                        return Configs.IMAGE_PREFIX + actionIcon.image_set.default;
                    }
                }
                return null;
            };
            return {
                up: getActionIcon(customParams.custom_icon?.action_open_icon_id) || upButton,
                down: getActionIcon(customParams.custom_icon?.action_close_icon_id) || downButton,
                stop: getActionIcon(customParams.custom_icon?.action_stop_icon_id) || equalButton,
            };
        }
        return {
            up: upButton,
            down: downButton,
            stop: equalButton,
        };
    }, [controlIconTypeList, control]);

    return (
        <Column min={6} max={12}>
            <Widget style={{padding: 0}}>
                <div className="position-relative">
                    <div className="curtain">
                        <img src={icon} alt="curtain"/>
                        <div className=" curtain-title">
                            <span>{control.name}</span>
                        </div>
                        <div className="curtain-buttons">
                            <div className="control-button">
                                <button {...onClickHandler(() => click(PhaseStatus.Up))}>
                                    <img src={actionIcons.up} alt="up"/>
                                </button>
                            </div>
                            {
                                renderIf(!isTwoPhase)(() => (
                                    <div className="control-button">
                                        <button {...onClickHandler(() => click(PhaseStatus.Stop))}>
                                            <img src={actionIcons.stop} alt="stop"/>
                                        </button>
                                    </div>
                                ))
                            }
                            <div className="control-button">
                                <button {...onClickHandler(() => click(PhaseStatus.Down))}>
                                    <img src={actionIcons.down} alt="down"/>
                                </button>
                            </div>
                        </div>
                    </div>
                    {children}
                </div>
            </Widget>
        </Column>
    );
});
